import { useState, useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { jobService } from '@core/supabase/services/jobService';
import { clientService } from '@core/supabase/services/clientService';
import { isClientReadyForRecruitment } from '@core/clients/clientEngagementGate';
import { skillTaxonomyService, SkillSuggestion } from '@core/supabase/services/skillTaxonomyService';
import { Job, Client, Requisition } from '@core/types';
import type { JobStatus } from '@core/enums';
import {
  JOB_OPS_STATUS_LABELS,
  isJobOpsTransitionAllowed,
  jobOpsStatusEditOptions,
} from '@core/constants/jobLifecycle';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';

/** Module-level: inner `const FormRow = …` remounts children every render (one-character typing bug). */
function JobFormRow({
  label,
  children,
  span = 1,
}: {
  label: ReactNode;
  children: React.ReactNode;
  span?: 1 | 2;
}) {
  return (
    <div
      className={`grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem] last:border-b-0 ${
        span === 2 ? 'md:col-span-2' : ''
      }`}
    >
      <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function JobFormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <div className="ggj-profile-section-h">{title}</div>
      <div className="ggj-profile-section-body">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">{children}</div>
      </div>
    </>
  );
}

export interface JobFormProps {
  mode: 'new' | 'edit';
  job?: Job | null;
  initialClientId?: string;
  /** When creating a job from a requisition, pre-fills title, JD text, location, skills, client. */
  prefillRequisition?: Requisition | null;
  readOnly?: boolean;
  onSave: (job: Job) => void;
  onDelete?: () => void;
  orgId: string;
  userId: string;
}

export function JobForm({
  mode,
  job,
  initialClientId,
  prefillRequisition,
  readOnly = false,
  onSave,
  onDelete,
  orgId,
  userId,
}: JobFormProps) {
  const disabled = readOnly;
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [skillSuggestions, setSkillSuggestions] = useState<SkillSuggestion[]>([]);
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const skillSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [jdAiOpen, setJdAiOpen] = useState(false);
  const [careerSeoAiOpen, setCareerSeoAiOpen] = useState(false);

  const [form, setForm] = useState({
    client_id: job?.client_id ?? '',
    title: job?.title ?? '',
    description: job?.description ?? '',
    career_seo_summary: job?.career_seo_summary ?? '',
    jd_url: job?.jd_url ?? '',
    location: job?.location ?? '',
    experience_min: job?.experience_min != null ? String(job.experience_min) : '',
    experience_max: job?.experience_max != null ? String(job.experience_max) : '',
    skills_required: (job?.skills_required ?? []) as string[],
    status: job?.status ?? 'open',
  });

  useEffect(() => {
    if (orgId) {
      clientService.list(orgId).then((r) => setClients(Array.isArray(r) ? r : (r as { data: Client[] }).data));
    }
  }, [orgId]);

  useEffect(() => {
    if (initialClientId && mode === 'new' && !job) {
      setForm((f) => (f.client_id ? f : { ...f, client_id: initialClientId }));
    }
  }, [initialClientId, mode, job]);

  useEffect(() => {
    if (!prefillRequisition || mode !== 'new' || job) return;
    setForm((f) => ({
      ...f,
      client_id: prefillRequisition.client_id || f.client_id,
      title: prefillRequisition.title?.trim() || f.title,
      description: prefillRequisition.business_justification?.trim() || f.description,
      location: prefillRequisition.location?.trim() || f.location,
      skills_required:
        prefillRequisition.must_have_skills?.length > 0
          ? [...prefillRequisition.must_have_skills]
          : f.skills_required,
    }));
  }, [prefillRequisition?.id, mode, job]);

  useEffect(() => {
    if (job) {
      setForm({
        client_id: job.client_id ?? '',
        title: job.title ?? '',
        description: job.description ?? '',
        career_seo_summary: job.career_seo_summary ?? '',
        jd_url: job.jd_url ?? '',
        location: job.location ?? '',
        experience_min: job.experience_min != null ? String(job.experience_min) : '',
        experience_max: job.experience_max != null ? String(job.experience_max) : '',
        skills_required: (job.skills_required ?? []) as string[],
        status: job.status ?? 'open',
      });
    }
  }, [job?.id]);

  const set = (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSkillInputChange = (val: string) => {
    setSkillInput(val);
    if (skillSearchTimer.current) clearTimeout(skillSearchTimer.current);
    if (val.trim().length < 1) {
      setSkillSuggestions([]);
      setShowSkillDropdown(false);
      return;
    }
    skillSearchTimer.current = setTimeout(async () => {
      const results = await skillTaxonomyService.search(orgId, val);
      setSkillSuggestions(results);
      setShowSkillDropdown(results.length > 0);
    }, 200);
  };

  const addSkill = (override?: string) => {
    const s = (override ?? skillInput).trim();
    if (s && !form.skills_required.includes(s)) {
      setForm((f) => ({ ...f, skills_required: [...f.skills_required, s] }));
    }
    setSkillInput('');
    setSkillSuggestions([]);
    setShowSkillDropdown(false);
  };

  const removeSkill = (skill: string) =>
    setForm((f) => ({ ...f, skills_required: f.skills_required.filter((s) => s !== skill) }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Job title is required'); return; }
    if (!form.client_id) { setError('Please select a client'); return; }
    const selectedClient = clients.find((c) => c.id === form.client_id);
    if (mode === 'new' && !isClientReadyForRecruitment(selectedClient)) {
      setError(
        'This client is still in onboarding. Complete compliance on the client page before creating a job.'
      );
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (mode === 'new') {
        const created = await jobService.create({
          org_id: orgId,
          client_id: form.client_id,
          title: form.title.trim(),
          description: form.description || null,
          jd_url: form.jd_url || null,
          skills_required: form.skills_required,
          experience_min: form.experience_min ? parseInt(form.experience_min) : null,
          experience_max: form.experience_max ? parseInt(form.experience_max) : null,
          location: form.location || null,
          status: 'open',
          publish_status: 'draft',
          published_at: null,
          publish_channels: [],
          scheduled_publish_at: null,
          scheduled_publish_job_id: null,
          opened_at: new Date().toISOString().split('T')[0],
          filled_at: null,
          created_by: userId,
        });
        onSave(created);
      } else if (job) {
        const nextStatus = form.status as JobStatus;
        if (nextStatus !== job.status && !isJobOpsTransitionAllowed(job.status, nextStatus)) {
          setError(
            `Cannot change job status from "${JOB_OPS_STATUS_LABELS[job.status]}" to "${JOB_OPS_STATUS_LABELS[nextStatus]}".`
          );
          setLoading(false);
          return;
        }
        const updated = await jobService.update(job.id, {
          client_id: form.client_id,
          title: form.title.trim(),
          description: form.description || null,
          career_seo_summary: form.career_seo_summary.trim() || null,
          jd_url: form.jd_url || null,
          skills_required: form.skills_required,
          experience_min: form.experience_min ? parseInt(form.experience_min) : null,
          experience_max: form.experience_max ? parseInt(form.experience_max) : null,
          location: form.location || null,
          status: form.status,
        });
        onSave(updated);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <form id="job-form" onSubmit={handleSubmit} className="py-2">
      <JobFormSection title="Job Details">
        <JobFormRow label="Client *">
          <select className="input w-full max-w-md" value={form.client_id} onChange={set('client_id')} disabled={mode === 'edit' || disabled}>
            <option value="">Select client company</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </JobFormRow>
        <JobFormRow label="Job Title *">
          <input className="input w-full max-w-md" placeholder="Senior Java Developer" value={form.title} onChange={set('title')} disabled={disabled} />
        </JobFormRow>
        <JobFormRow label="Location">
          <input className="input w-full max-w-md" placeholder="Bengaluru / Remote" value={form.location} onChange={set('location')} disabled={disabled} />
        </JobFormRow>
        <JobFormRow label="Min Exp (yrs)">
          <input className="input w-full max-w-md" type="number" min="0" placeholder="3" value={form.experience_min} onChange={set('experience_min')} disabled={disabled} />
        </JobFormRow>
        <JobFormRow label="Max Exp (yrs)">
          <input className="input w-full max-w-md" type="number" min="0" placeholder="8" value={form.experience_max} onChange={set('experience_max')} disabled={disabled} />
        </JobFormRow>
        {mode === 'edit' && job && (
          <JobFormRow label="Status">
            <div>
              <select className="input w-full max-w-md" value={form.status} onChange={set('status')} disabled={disabled}>
                {jobOpsStatusEditOptions(job.status).map((v) => (
                  <option key={v} value={v}>
                    {JOB_OPS_STATUS_LABELS[v]}
                  </option>
                ))}
              </select>
              <p className="text-xs vscode-text-tertiary mt-2 max-w-md leading-relaxed">{/* PRD §9.4 */}
                Allowed moves follow the job ops lifecycle. Filled is final unless you use another tool to correct data.
              </p>
            </div>
          </JobFormRow>
        )}
      </JobFormSection>

      <JobFormSection title="Job Description">
        <JobFormRow label="JD URL" span={2}>
          <input className="input w-full max-w-lg" placeholder="https://linkedin.com/jobs/..." value={form.jd_url} onChange={set('jd_url')} disabled={disabled} />
        </JobFormRow>
        <JobFormRow
          label={
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>Description</span>
              {!disabled && (
                <AiAssistIconButton onClick={() => setJdAiOpen(true)} title="AI assist — job description" />
              )}
            </span>
          }
          span={2}
        >
          <textarea className="input w-full resize-none" rows={5} placeholder="Paste job description here..." value={form.description} onChange={set('description')} disabled={disabled} />
        </JobFormRow>
        <JobFormRow
          label={
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>Career page summary / SEO</span>
              {!disabled && (
                <AiAssistIconButton
                  onClick={() => setCareerSeoAiOpen(true)}
                  title="AI assist — public career listing blurb (PRD §11.1)"
                />
              )}
            </span>
          }
          span={2}
        >
          <textarea
            className="input w-full resize-none"
            rows={3}
            placeholder="Short public blurb shown on your careers site (optional). Separate from full JD."
            value={form.career_seo_summary}
            onChange={set('career_seo_summary')}
            disabled={disabled}
          />
          <p className="text-xs vscode-text-tertiary mt-1 max-w-xl">
            Used on published job cards when set (migration <span className="font-mono">060</span>). Keep candidate-facing;
            no internal codes.
          </p>
        </JobFormRow>
      </JobFormSection>

      <JobFormSection title="Required Skills">
        <JobFormRow label="Required skills" span={2}>
          <div className="space-y-2">
            {/* Chips — always shown */}
            <div className="flex flex-wrap gap-1.5 min-h-[28px]">
              {form.skills_required.length > 0 ? (
                form.skills_required.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{ background: 'rgba(139,128,249,0.1)', color: '#8B80F9', border: '1px solid rgba(139,128,249,0.3)' }}>
                    {s}
                    {!disabled && (
                      <button type="button" onClick={() => removeSkill(s)}
                        className="ml-0.5 hover:opacity-60" aria-label={`Remove ${s}`}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))
              ) : (
                <span className="text-sm vscode-text-tertiary">No skills added yet.</span>
              )}
            </div>

            {/* Add input — only in edit mode */}
            {!disabled && (
              <div className="relative flex gap-2 max-w-sm mt-1">
                <div className="relative flex-1">
                  <input
                    className="input w-full text-sm"
                    placeholder="Type a skill and press Enter or Add…"
                    value={skillInput}
                    onChange={(e) => handleSkillInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addSkill(); }
                      if (e.key === 'Escape') setShowSkillDropdown(false);
                    }}
                    onBlur={() => setTimeout(() => setShowSkillDropdown(false), 150)}
                    autoComplete="off"
                  />
                  {showSkillDropdown && (
                    <ul className="absolute z-10 mt-1 w-full rounded-lg border vscode-border vscode-bg-elevated shadow-xl max-h-48 overflow-y-auto">
                      {skillSuggestions
                        .filter((s) => !form.skills_required.includes(s.name))
                        .map((s) => (
                          <li key={s.id}
                            className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-[var(--vscode-bg-tertiary)] vscode-text-primary"
                            onMouseDown={() => addSkill(s.name)}>
                            <span>{s.name}</span>
                            {s.category && <span className="text-xs vscode-text-tertiary">{s.category}</span>}
                          </li>
                        ))}
                      {skillInput.trim() && !skillSuggestions.find((s) => s.name.toLowerCase() === skillInput.trim().toLowerCase()) && (
                        <li className="px-3 py-2 text-sm cursor-pointer hover:bg-[var(--vscode-bg-tertiary)] text-[var(--vscode-primary)] italic"
                          onMouseDown={() => addSkill(skillInput.trim())}>
                          Add &quot;{skillInput.trim()}&quot; as custom skill
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                <button type="button" className="btn-secondary shrink-0 text-sm" onClick={() => addSkill()}>Add</button>
              </div>
            )}
          </div>
        </JobFormRow>
      </JobFormSection>

      {error && (
        <div className="mx-4 my-2 px-3 py-2 rounded-lg border border-red-400/40 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {mode === 'new' && (
        <div className="flex gap-2 flex-wrap">
          <Link to="/jobs" className="btn-secondary">[D] Discard</Link>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : '[S] Save'}
          </button>
        </div>
      )}
    </form>

    <ContextAiAssistModal
      open={jdAiOpen}
      onClose={() => setJdAiOpen(false)}
      orgId={orgId}
      feature="ai_assist_job_description"
      preset="job_description"
      title="AI — job description"
      contextHint={`Title: ${form.title}\nLocation: ${form.location}\nSkills: ${form.skills_required.join(', ')}`}
      original={form.description}
      onApply={(text, mode) =>
        setForm((f) => ({
          ...f,
          description:
            mode === 'replace'
              ? text
              : f.description.trim()
                ? `${f.description.trim()}\n\n${text}`
                : text,
        }))
      }
    />
    <ContextAiAssistModal
      open={careerSeoAiOpen}
      onClose={() => setCareerSeoAiOpen(false)}
      orgId={orgId}
      feature="ai_assist_career_seo_summary"
      preset="career_seo_public_listing"
      title="AI — career page summary"
      contextHint={`Title: ${form.title}\nLocation: ${form.location}\nSkills: ${form.skills_required.join(', ')}\nJD excerpt:\n${(form.description || '').slice(0, 1200)}`}
      original={form.career_seo_summary}
      onApply={(text, mode) =>
        setForm((f) => ({
          ...f,
          career_seo_summary:
            mode === 'replace'
              ? text
              : f.career_seo_summary.trim()
                ? `${f.career_seo_summary.trim()}\n\n${text}`
                : text,
        }))
      }
    />
  </>
  );
}
